import { useEffect, useState } from "react";
import type { JobDto, MaterialDto, SupplierDto } from "@grizzly/shared";
import { api, ApiClientError } from "../../lib/apiClient.js";
import { Button } from "../ui/button.js";
import { Modal } from "../ui/modal.js";
import { SelectInput, TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";
import { Plus, Trash2 } from "lucide-react";
import { useI18n } from "../../i18n/index.js";

interface Line {
  materialId: string;
  description: string;
  quantity: string;
  unitCost: string;
}

export function PurchaseOrderDialog({
  open,
  onOpenChange,
  suppliers,
  jobs,
  materials,
  onCreated,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  suppliers: SupplierDto[];
  jobs: JobDto[];
  materials: MaterialDto[];
  onCreated(): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [supplierId, setSupplierId] = useState("");
  const [jobId, setJobId] = useState("");
  const [lines, setLines] = useState<Line[]>([
    { materialId: "", description: "", quantity: "1", unitCost: "" },
  ]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setSupplierId(suppliers[0]?.id ?? "");
  }, [open, suppliers]);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.createPurchaseOrder({
        supplierId,
        jobId: jobId || "",
        lines: lines
          .filter((l) => l.materialId)
          .map((l) => {
            const material = materials.find((m) => m.id === l.materialId);
            return {
              materialId: l.materialId,
              description: l.description || material?.name || "",
              quantity: l.quantity,
              unitCost: l.unitCost,
            };
          }),
      });
      push(t("purchasing.createdToast"), res.order.number);
      setLines([{ materialId: "", description: "", quantity: "1", unitCost: "" }]);
      setJobId("");
      onOpenChange(false);
      onCreated();
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("purchasing.newPurchaseOrder")}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={busy || !supplierId || !lines.some((l) => l.materialId)}
          >
            {t("purchasing.createOrder")}
          </Button>
        </>
      }
    >
      <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto pe-1">
        <div className="grid gap-3 sm:grid-cols-2">
          <SelectInput
            label={t("purchasing.supplier")}
            options={suppliers.map((s) => ({ value: s.id, label: s.name }))}
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
          />
          <SelectInput
            label={t("purchasing.forJob")}
            options={[
              { value: "", label: t("purchasing.noneStockOrder") },
              ...jobs.map((j) => ({ value: j.id, label: `${j.number} · ${j.title}` })),
            ]}
            value={jobId}
            onChange={(e) => setJobId(e.target.value)}
          />
        </div>

        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ink">{t("purchasing.lines")}</h3>
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              setLines((l) => [
                ...l,
                { materialId: "", description: "", quantity: "1", unitCost: "" },
              ])
            }
          >
            <Plus className="h-4 w-4" aria-hidden="true" /> {t("purchasing.addLine")}
          </Button>
        </div>

        {lines.map((line, idx) => (
          <div key={idx} className="rounded border border-line p-2">
            <div className="grid gap-2 sm:grid-cols-2">
              <SelectInput
                label={t("common.fields.material")}
                options={materials.map((m) => ({
                  value: m.id,
                  label: `${m.sku} · ${m.name}`,
                }))}
                value={line.materialId}
                onChange={(e) =>
                  setLines((l) =>
                    l.map((x, i) =>
                      i === idx ? { ...x, materialId: e.target.value } : x
                    )
                  )
                }
              />
              <TextInput
                label={t("common.fields.description")}
                value={line.description}
                onChange={(e) =>
                  setLines((l) =>
                    l.map((x, i) =>
                      i === idx ? { ...x, description: e.target.value } : x
                    )
                  )
                }
              />
              <TextInput
                label={t("common.fields.quantity")}
                value={line.quantity}
                onChange={(e) =>
                  setLines((l) =>
                    l.map((x, i) => (i === idx ? { ...x, quantity: e.target.value } : x))
                  )
                }
              />
              <TextInput
                label={t("purchasing.unitCost")}
                value={line.unitCost}
                onChange={(e) =>
                  setLines((l) =>
                    l.map((x, i) => (i === idx ? { ...x, unitCost: e.target.value } : x))
                  )
                }
              />
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setLines((l) => l.filter((_, i) => i !== idx))}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" /> {t("common.remove")}
            </Button>
          </div>
        ))}
      </div>
    </Modal>
  );
}
